import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

import { HERO_LAYOUTS, type HeroLayout } from '@groove/core';

// The layout vocabulary is owned by @groove/core (framework-neutral, so the
// coverage engine can read it without pulling in @angular/core). Re-exported
// here so consumers can keep importing it from the component.
export { HERO_LAYOUTS, type HeroLayout };

/**
 * `groove-hero` — the full-bleed photographic hero. The flagship pattern: a
 * photographic backdrop, a scene-correct contrast scrim, and bottom-anchored
 * projected content (headline / lede / actions).
 *
 * STRUCTURE THE COMPONENT OWNS
 *   1. A full-bleed region (`min-height` input, default ~78vh).
 *   2. The media `<img>` — the LCP element (see LCP DESIGN FACTS below).
 *   3. The scrim layer — a radial-ellipse + linear-bottom-fade composite, keyed
 *      INTO `var(--groove-color-bg-page)` so the hero bleeds into whatever
 *      scene hosts it (scene-correct by construction).
 *   4. Bottom-anchored `<ng-content>` — the consumer composes the headline /
 *      lede / actions; the hero supplies max-width + padding + z-layering.
 *
 * SCENE-AGNOSTIC BY CONSTRUCTION
 *   The scrim fades into `--groove-color-bg-page` and text reads via
 *   `--groove-color-fg-default`, so the hero renders correctly on whatever
 *   `[data-scene]` wraps it. Consumers typically wrap it in
 *   `data-scene="stage"` (the warm-dark room), but nothing here hardcodes it.
 *
 * TYPOGRAPHY
 *   The headline is projected, so the component is type-agnostic. The intended
 *   composition is `.groove-type-display` unchanged, matching the sitewide
 *   display and heading roles — one consistent voice rather than a bespoke
 *   hero face. Legibility over photography is governed by the scrim floor
 *   below, not by the type treatment.
 *
 * LCP DESIGN FACTS (obligations a consuming page inherits)
 *   - The media `<img>` carries `fetchpriority="high"` and is NEVER lazy-loaded
 *     (no `loading="lazy"`) — it is the hero/LCP element.
 *   - `decoding="async"` keeps it off the main-thread critical path.
 *   - The consuming page SHOULD add a `<link rel="preload" as="image">` for the
 *     chosen crop (with `imagesrcset`/`imagesizes` matching the `srcset`/
 *     `sizes` inputs) in the page head — the component cannot emit head links.
 *   - The region reserves height via `min-height` (no CLS); pass an
 *     `aspect-ratio`-derived height tier at the page level if a fixed ratio is
 *     wanted.
 *
 * SCRIM CONTRAST GUARANTEE
 *   The scrim's opaque bottom band makes fg-default over the content zone
 *   >= 4.5:1 TRUE BY CONSTRUCTION rather than by tuning. The guarantee is
 *   enforced by a worst-case (white-image) probe in the validation harness;
 *   the final gradient stops are documented in the hero CSS shipped by
 *   @groove/styles.
 */
@Component({
  selector: 'groove-hero',
  standalone: true,
  template: `
    <div class="groove-hero-media-wrap">
      <img
        class="groove-hero-media"
        [src]="src()"
        [attr.srcset]="srcset() || null"
        [attr.sizes]="sizes() || null"
        [alt]="alt()"
        fetchpriority="high"
        decoding="async"
      />
    </div>
    @if (layout() === 'bleed') {
      <div class="groove-hero-scrim" aria-hidden="true"></div>
    }
    <div class="groove-hero-content">
      <ng-content />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'groove-hero',
    '[attr.data-layout]': 'layoutAttr()',
    '[style.--_hero-min-height]': 'minHeight()',
    // object-position rides a host custom property (not an inline style on
    // the img): browsers serialize inline style attributes in different
    // positions, which made DOM snapshots browser-order-sensitive.
    '[style.--_hero-object-position]': 'objectPosition()',
  },
})
export class GrooveHeroComponent {
  /**
   * Layout (scene-agnostic):
   *   - bleed (default) — full-bleed photographic backdrop + scrim, projected
   *     content bottom-anchored over the media. The scrim contrast
   *     guarantee applies here.
   *   - split — the contained treatment: projected content on clean
   *     ground beside a contained media figure (no scrim, no overlay). Used
   *     when the text must read instantly and the image stays near its native
   *     resolution rather than upscaled across a full bleed.
   */
  readonly layout = input<HeroLayout>('bleed');
  /** Media source — the default crop. Required: a hero is never image-less. */
  readonly src = input.required<string>();
  /** Responsive candidate set for art direction; empty → attribute omitted. */
  readonly srcset = input<string>('');
  /** `sizes` descriptor paired with `srcset`; empty → attribute omitted. */
  readonly sizes = input<string>('');
  /**
   * Alt text — REQUIRED. The media is meaningful content (WCAG 1.1.1); the hero
   * deliberately does not allow an image with no described purpose.
   */
  readonly alt = input.required<string>();
  /** Focal point for the cover crop; default keeps the subject upper-right. */
  readonly objectPosition = input<string>('70% 30%');
  /** Reserved region height (no CLS). */
  readonly minHeight = input<string>('78vh');

  // Omit the default attr to keep the DOM clean (omit-default-attribute pattern).
  protected readonly layoutAttr = computed(() =>
    this.layout() === 'bleed' ? null : this.layout(),
  );
}
